import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { todayISO, formatFriendlyDate, parseISODate, toISODate } from "@/lib/dateUtils";
import { toast } from "sonner";
import {
  DollarSign,
  Loader2,
  Send,
  Pencil,
  Trash2,
  CalendarDays,
  Info,
} from "lucide-react";

// Default a fresh commission to "last Monday → last Sunday" so the freelancer
// reports the just-completed week. They can override either date if they
// settle commissions on a different cadence.
function lastWeekRange() {
  const today = parseISODate(todayISO()) || new Date();
  // getDay: 0=Sun..6=Sat; Monday-anchored weekday: 0=Mon..6=Sun
  const wd = today.getDay() === 0 ? 6 : today.getDay() - 1;
  const start = new Date(today);
  start.setDate(today.getDate() - wd - 7); // Monday of the previous week
  const end = new Date(start);
  end.setDate(start.getDate() + 6); // following Sunday
  return { start: toISODate(start), end: toISODate(end) };
}

function statusStyle(status) {
  switch (status) {
    case "submitted":
      return { bg: "#f59e0b22", color: "#b45309", label: "Submitted" };
    case "approved":
      return { bg: `${BRAND.teal}22`, color: BRAND.tealDark, label: "Approved" };
    case "returned":
      return { bg: "#ef444422", color: "#b91c1c", label: "Needs revision" };
    case "draft":
    default:
      return { bg: "#94a3b820", color: "#475569", label: "Draft" };
  }
}

export default function CommissionsSection() {
  const [settings, setSettings] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // commission being edited

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, l] = await Promise.all([
        api.get("/settings/commissions"),
        api.get("/commissions"),
      ]);
      setSettings(s.data);
      setList(l.data?.commissions || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load commissions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mt-8" data-testid="commissions-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2">
            <DollarSign size={14} style={{ color: BRAND.teal }} />
            Commissions
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            Report total booking fees collected; admin approval converts them
            into hours on your timesheet.
          </p>
        </div>
        {settings && (
          <div
            className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500 px-2.5 py-1 rounded-md bg-white border border-slate-200"
            title="These rates can change — your historical commissions keep their original split."
          >
            <Info size={12} />${settings.booking_fee.toFixed(2)} per booking ·{" "}
            {Math.round(settings.commission_split * 100)}% to you
          </div>
        )}
      </div>

      <CommissionForm
        settings={settings}
        editing={editing}
        onCancelEdit={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />

      {error && (
        <div
          className="mt-4 px-3 py-2 text-sm rounded-md bg-red-50 border border-red-200 text-red-700"
          data-testid="commissions-error"
        >
          {error}
        </div>
      )}

      <CommissionsList
        list={list}
        loading={loading}
        onEdit={(c) => setEditing(c)}
        onChanged={load}
      />
    </div>
  );
}

function CommissionForm({ settings, editing, onCancelEdit, onSaved }) {
  const defaults = useMemo(lastWeekRange, []);
  const isEdit = !!editing;
  const [periodStart, setPeriodStart] = useState(editing?.period_start || defaults.start);
  const [periodEnd, setPeriodEnd] = useState(editing?.period_end || defaults.end);
  const [totalFees, setTotalFees] = useState(
    editing?.total_fees != null ? String(editing.total_fees) : ""
  );
  const [notes, setNotes] = useState(editing?.notes || "");
  const [submitting, setSubmitting] = useState(false);

  // Reset fields when the edit target changes (or is cleared).
  useEffect(() => {
    if (editing) {
      setPeriodStart(editing.period_start);
      setPeriodEnd(editing.period_end);
      setTotalFees(String(editing.total_fees));
      setNotes(editing.notes || "");
    } else {
      const fresh = lastWeekRange();
      setPeriodStart(fresh.start);
      setPeriodEnd(fresh.end);
      setTotalFees("");
      setNotes("");
    }
  }, [editing]);

  const fees = parseFloat(totalFees);
  const validFees = Number.isFinite(fees) && fees >= 0;
  const split = settings?.commission_split ?? 0.5;
  const previewAmount = validFees ? Math.round(fees * split * 100) / 100 : null;
  const bookingFee = settings?.booking_fee ?? 0;
  const approxBookings =
    validFees && bookingFee > 0 ? Math.round(fees / bookingFee) : null;

  const submit = async (action) => {
    if (!validFees) {
      toast.error("Enter a non-negative dollar amount for total booking fees.");
      return;
    }
    if (!periodStart || !periodEnd) {
      toast.error("Pick both a period start and end date.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        period_start: periodStart,
        period_end: periodEnd,
        total_fees: fees,
        notes: notes.trim() || null,
      };
      let saved;
      if (isEdit) {
        const { data } = await api.put(`/commissions/${editing.id}`, body);
        saved = data;
      } else {
        const { data } = await api.post("/commissions", body);
        saved = data;
      }
      if (action === "submit") {
        await api.post(`/commissions/${saved.id}/submit`);
        toast.success("Commission submitted for review");
      } else {
        toast.success(isEdit ? "Commission updated" : "Saved as draft");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save commission.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 p-5"
      data-testid="commission-form"
    >
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-slate-800">
          {isEdit ? "Edit commission" : "New commission"}
        </h4>
        {isEdit && (
          <button
            type="button"
            onClick={onCancelEdit}
            className="text-xs text-slate-500 hover:text-slate-800"
          >
            Cancel edit
          </button>
        )}
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Period start (Mon)
          </label>
          <input
            type="date"
            value={periodStart}
            max={periodEnd || todayISO()}
            onChange={(e) => setPeriodStart(e.target.value)}
            data-testid="commission-period-start"
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2"
            style={{ "--tw-ring-color": BRAND.teal }}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Period end (Sun)
          </label>
          <input
            type="date"
            value={periodEnd}
            min={periodStart}
            max={todayISO()}
            onChange={(e) => setPeriodEnd(e.target.value)}
            data-testid="commission-period-end"
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2"
            style={{ "--tw-ring-color": BRAND.teal }}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Total booking fees collected
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
              $
            </span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={totalFees}
              onChange={(e) => setTotalFees(e.target.value)}
              placeholder="e.g. 190.00"
              data-testid="commission-total-fees"
              className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2"
              style={{ "--tw-ring-color": BRAND.teal }}
              disabled={submitting}
            />
          </div>
        </div>
      </div>

      <div className="mt-3">
        <label className="block text-xs font-medium text-slate-600 mb-1">
          Notes (optional)
        </label>
        <textarea
          rows={2}
          value={notes}
          maxLength={500}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything the admin should know about this period…"
          data-testid="commission-notes"
          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 resize-none"
          style={{ "--tw-ring-color": BRAND.teal }}
          disabled={submitting}
        />
      </div>

      {/* Preview */}
      {previewAmount != null && (
        <div
          className="mt-3 rounded-lg border p-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1"
          style={{
            background: `${BRAND.teal}08`,
            borderColor: `${BRAND.teal}40`,
          }}
          data-testid="commission-preview"
        >
          <span className="text-slate-700">
            Commission ({Math.round(split * 100)}%):{" "}
            <strong style={{ color: BRAND.teal }}>
              ${previewAmount.toFixed(2)}
            </strong>
          </span>
          {approxBookings != null && approxBookings > 0 && (
            <span className="text-slate-500 text-xs">
              ≈ {approxBookings} booking{approxBookings === 1 ? "" : "s"} at $
              {bookingFee.toFixed(2)} each
            </span>
          )}
          <span className="text-slate-500 text-xs">
            Hours added on approval at your hourly rate.
          </span>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2 justify-end">
        <button
          type="button"
          onClick={() => submit("save")}
          disabled={submitting}
          data-testid="commission-save-draft"
          className="px-4 py-2 rounded-lg text-slate-700 border border-slate-200 hover:bg-slate-50 transition disabled:opacity-60"
        >
          {submitting ? <Loader2 className="animate-spin inline" size={14} /> : "Save draft"}
        </button>
        <button
          type="button"
          onClick={() => submit("submit")}
          disabled={submitting}
          data-testid="commission-submit"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold transition disabled:opacity-60"
          style={{
            background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
          }}
        >
          {submitting ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
          {isEdit ? "Save & resubmit" : "Submit for review"}
        </button>
      </div>
    </div>
  );
}

function CommissionsList({ list, loading, onEdit, onChanged }) {
  if (loading) {
    return (
      <div className="mt-4 bg-white rounded-2xl border border-slate-200 p-8 flex items-center justify-center">
        <Loader2 className="animate-spin text-slate-400" size={18} />
      </div>
    );
  }
  if (!list || list.length === 0) {
    return (
      <div
        className="mt-4 bg-white rounded-2xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500"
        data-testid="commissions-empty"
      >
        No commissions yet. Use the form above to log your first period.
      </div>
    );
  }

  const handleDelete = async (c) => {
    try {
      await api.delete(`/commissions/${c.id}`);
      toast.success("Commission deleted");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete commission.");
    }
  };

  return (
    <div
      className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-hidden"
      data-testid="commissions-list"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <th className="text-left font-semibold px-4 py-3">Period</th>
              <th className="text-right font-semibold px-4 py-3">Fees</th>
              <th className="text-right font-semibold px-4 py-3">Commission</th>
              <th className="text-right font-semibold px-4 py-3">Hours</th>
              <th className="text-center font-semibold px-4 py-3">Status</th>
              <th className="text-right font-semibold px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => {
              const s = statusStyle(c.status);
              const editable = c.status === "draft" || c.status === "returned";
              return (
                <tr
                  key={c.id}
                  className="border-t border-slate-100 hover:bg-slate-50/50 transition"
                  data-testid={`commission-row-${c.id}`}
                >
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <CalendarDays size={12} className="text-slate-400" />
                      {formatFriendlyDate(c.period_start)} –{" "}
                      {formatFriendlyDate(c.period_end)}
                    </div>
                    {c.return_reason && (
                      <div
                        className="mt-1 text-[11px] text-red-700"
                        data-testid={`commission-return-reason-${c.id}`}
                      >
                        Returned: {c.return_reason}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                    ${Number(c.total_fees).toFixed(2)}
                  </td>
                  <td
                    className="px-4 py-3 text-right tabular-nums font-semibold"
                    style={{ color: BRAND.teal }}
                  >
                    ${Number(c.commission_amount).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                    {c.equivalent_hours != null
                      ? `${Number(c.equivalent_hours).toFixed(2)}h`
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ background: s.bg, color: s.color }}
                    >
                      {s.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {editable && (
                        <>
                          <button
                            onClick={() => onEdit(c)}
                            data-testid={`commission-edit-${c.id}`}
                            className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded transition"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(c)}
                            data-testid={`commission-delete-${c.id}`}
                            className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                      {!editable && (
                        <span className="text-xs text-slate-400">Locked</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
